/**
 * @file OPM 数据适配器 - 实现 OPMEditor（LogicFlow）和 OPMEditorCV（Cytoscape）数据格式的双向转换
 *
 * 目标：让 OPMEditorCV 组件能够兼容 OPMEditor 的老数据结构，实现平滑替换
 */

import { EdgeType, LinkNegation, LinkQualifier } from '../model/edge-model';
import { buildMarkerLabel } from '../options/link-marker-config';
import {
  Affiliation,
  Essence,
  ChildGroupType,
  type ProcessAutonomy,
} from '../model/node-model';
import { recalculateNamespacesForPlainNodes } from './node-namespace';
import {
  buildNodeDefinitionItems,
  buildOplContext,
  buildRelationItems,
  OplItem,
  OplNodeRecord,
  OplEdgeRecord,
} from './opl-rules';

// ============================================================================
// OPMEditor 数据结构类型定义（老格式 - LogicFlow）
// ============================================================================

export interface OPMGraphData {
  nodes: OPMNode[];
  edges: OPMEdge[];
  _timestamp?: number;
  // OPL 语句列表（与该图层关联），供保存/初始化复用
  opl?: OplItem[];
}

export interface OPMNode {
  id: string;
  type: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  text?:
    | {
        x: number;
        y: number;
        value: string;
      }
    | string;
  properties?: {
    name?: string;
    description?: string;
    namespace?: string;
    packageName?: string;
    codeName?: string;
    tags?: string[];
    objectType?: string;
    exampleValues?: string;
    autonomy?: ProcessAutonomy;
    version?: string;
    updatedAt?: string;
    updatedBy?: string[];
    relatedSystem?: string;
    nodeType?: string;
    width?: number;
    height?: number;
    isState?: boolean;
    isValue?: boolean;
    parentGroupId?: string;
    isGroup?: boolean;
    isEntityGroup?: boolean;
    originalType?: string;
    children?: string[];
    isRestrict?: boolean;
    autoResize?: boolean;
    collapsible?: boolean;
    isCollapsed?: boolean;
    isSubgraphRoot?: boolean;
    linkedLayerId?: string;
    hasChildSubgraph?: boolean;
    // OPMEditorCV 扩展字段（兼容保存）
    essence?: Essence;
    affiliation?: Affiliation;
    isStructurePart?: boolean;
    isSubelementOfMain?: boolean;
    childGroupType?: ChildGroupType;
  };
  children?: string[];
}

export interface OPMEdge {
  id: string;
  type: string;
  sourceNodeId: string;
  targetNodeId: string;
  sourceAnchorId?: string;
  targetAnchorId?: string;
  startPoint?: { x: number; y: number };
  endPoint?: { x: number; y: number };
  pointsList?: Array<{ x: number; y: number }>;
  properties?: {
    edgeType?: string;
    // OPMEditorCV 扩展字段
    opmEdgeType?: EdgeType | string;
    propagation?: boolean;
    label?: string;
    tags?: string[];
    description?: string;
    version?: string;
    updatedAt?: string;
    updatedBy?: string[];
    instanceFileName?: string;
    instanceFileUrl?: string;
    linkQualifier?: LinkQualifier;
    linkNegation?: LinkNegation;
  };
  text?: {
    x: number;
    y: number;
    value: string;
  };
}

export interface OntologyGraphLayer {
  layerId: string;
  layerName: string;
  graphData: OPMGraphData;
  children: OntologyGraphLayer[];
  parentLayerId?: string;
  parentNodeId?: string;
  nodeIdPrefix?: string;
}

export type OntologyGraphTree = OntologyGraphLayer[];

export interface OntologyGraphPayload {
  version: 'v2';
  data: OntologyGraphTree;
}

// ============================================================================
// OPMEditorCV 数据结构类型定义（新格式 - Cytoscape Plain JSON）
// ============================================================================

export type PlainNodeType = 'object' | 'process' | 'state' | 'value';

export interface PlainNode {
  id: string;
  type: PlainNodeType;
  label: string;
  // 节点描述（可选，用于与 OPMEditor 兼容并支持扩展属性）
  description?: string;
  // 扩展字段（与描述同级，便于保存/导出）
  namespace?: string;
  packageName?: string;
  codeName?: string;
  tags?: string[];
  objectType?: string;
  exampleValues?: string;
  autonomy?: ProcessAutonomy;
  version?: string;
  updatedAt?: string;
  updatedBy?: string[];
  relatedSystem?: string;
  parentId: string | null;
  essence: Essence;
  affiliation: Affiliation;
  isStructurePart: boolean;
  isSubelementOfMain: boolean;
  deleted: boolean;
  diagramId: string | null;
  width: number | null;
  height: number | null;
  // 扩展字段：用于兼容 OPMEditor 格式
  _legacyType?: string;
  _legacyProperties?: Record<string, any>;
  childGroupType?: ChildGroupType;
}

export interface PlainEdge {
  id: string;
  sourceId: string;
  targetId: string;
  type: EdgeType;
  label: string;
  tags?: string[];
  description?: string;
  version?: string;
  updatedAt?: string;
  updatedBy?: string[];
  instanceFileName?: string;
  instanceFileUrl?: string;
  propagation: boolean;
  deleted: boolean;
  originalEdgeIds: string[];
  derivedEdgeIds: string[];
  preferedEdgeId: string | null;
  collection: 'original' | 'derived';
  // 扩展字段
  _legacyType?: string;
  _legacyProperties?: Record<string, any>;
  linkQualifier: LinkQualifier;
  linkNegation: LinkNegation;
}

export interface PlainDiagram {
  id: string;
  label: string | number;
  labelId: number;
  parentId: string | null;
  childIds: string[];
  mainNodeId: string | null;
  diagramJson: any;
  // 预生成的 OPL 语句（与图层一致），若缺失则保存时会重新生成
  opl?: OplItem[];
  // 扩展字段：保存 OPMEditor 层级信息
  _layerId?: string;
  _layerName?: string;
  _nodeIdPrefix?: string;
}

export interface PlainExportPayload {
  version: 1;
  nodes: PlainNode[];
  originalEdges: PlainEdge[];
  derivedEdges: PlainEdge[];
  diagrams: PlainDiagram[];
  rootDiagramId: string;
  eleCounter: string;
  // 扩展字段：标记是否从 OPMEditor 格式转换而来
  _convertedFrom?: 'OPMEditor';
}

// ============================================================================
// 类型映射表
// ============================================================================

// OPMEditor 节点类型 → OPMEditorCV 节点类型
const NODE_TYPE_TO_PLAIN: Record<string, PlainNodeType> = {
  rect: 'object',
  circle: 'process',
  // 旧版 OPMEditor 里 polygon 表示「过程」，这里必须映射为 process（否则如“试验”等节点会被当作对象）
  polygon: 'process',
  diamond: 'object',
  state: 'state',
  value: 'value',
  'dynamic-group': 'object',
};

// OPMEditorCV 节点类型 → OPMEditor 节点类型（默认映射）
const PLAIN_TYPE_TO_NODE: Record<PlainNodeType, string> = {
  object: 'rect',
  // 旧版 OPMEditor 的“过程”节点类型为 polygon（不是 circle）
  process: 'polygon',
  state: 'state',
  value: 'value',
};

// OPMEditor 边类型属性 → OPMEditorCV EdgeType
// 注意：老组件存储的是 UI 类型 ID（如 'middle-arrow'），不是语义名称
const EDGE_TYPE_MAP: Record<string, EdgeType> = {
  // UI 类型 ID（老组件 EdgeTypeDialog 中的 id）
  'middle-arrow': EdgeType.Aggregation, // 聚合关系
  'middle-arrow-double-triangle': EdgeType.Exhibition, // 展示关系
  'middle-arrow-hollow-triangle': EdgeType.Generalization, // 泛化关系
  'middle-arrow-triangle-circle': EdgeType.Classification, // 实例化关系
  // 约定：单向关联关系 -> Tagged；对等关联关系 -> bidirectional-arrow（用于显示双向箭头）
  polyline: EdgeType.Tagged, // 单向关联关系
  'bidirectional-arrow': EdgeType.Bidirectional, // 对等关联关系
  'Bidirectional-arrow': EdgeType.Bidirectional, // 兼容大小写不一致的旧数据

  // 语义名称（兼容旧的语义名称格式，以防万一）
  aggregation: EdgeType.Aggregation,
  'Aggregation-participation': EdgeType.Aggregation,
  exhibition: EdgeType.Exhibition,
  'Exhibition-characterization': EdgeType.Exhibition,
  generalization: EdgeType.Generalization,
  'Generalization-specialization': EdgeType.Generalization,
  classification: EdgeType.Classification,
  'Classification-instantiation': EdgeType.Classification,
  consumption: EdgeType.Consumption,
  Consumption: EdgeType.Consumption,
  effect: EdgeType.Effect,
  Effect: EdgeType.Effect,
  instrument: EdgeType.Instrument,
  Instrument: EdgeType.Instrument,
  agent: EdgeType.Agent,
  Agent: EdgeType.Agent,
  tagged: EdgeType.Tagged,
  generation: EdgeType.Generation,
  Generation: EdgeType.Generation,
  influence: EdgeType.Effect, // 兼容老数据：影响关系统一映射为 Effect
  Influence: EdgeType.Effect,
  invocation: EdgeType.Invocation,
  Invocation: EdgeType.Invocation,

  Tagged: EdgeType.Tagged,
};

// EdgeType → OPMEditor 边类型属性（导出时使用 UI 类型 ID）
const EDGE_TYPE_REVERSE_MAP: Record<EdgeType | 'Influence', string> = {
  [EdgeType.Aggregation]: 'middle-arrow', // 聚合关系
  [EdgeType.Exhibition]: 'middle-arrow-double-triangle', // 展示关系
  [EdgeType.Generalization]: 'middle-arrow-hollow-triangle', // 泛化关系
  [EdgeType.Classification]: 'middle-arrow-triangle-circle', // 实例化关系
  [EdgeType.Consumption]: 'consumption', // 消耗关系 → 单向关联
  [EdgeType.Bidirectional]: 'bidirectional-arrow', // 对等关联关系
  [EdgeType.Effect]: 'effect', // 影响关系 → Effect
  [EdgeType.Instrument]: 'instrument', // 工具关系 → 单向关联
  [EdgeType.Agent]: 'agent', // 代理关系 → 单向关联
  [EdgeType.Generation]: 'generation', // 生成关系 → 单向关联
  [EdgeType.Invocation]: 'invocation', // 调用关系 → 单向关联
  [EdgeType.Tagged]: 'polyline', // 标记关系 → 单向关联
  Influence: 'Effect', // 兼容老数据：旧 Influence 枚举统一回写为 Effect
};

// ============================================================================
// OPL 生成辅助（保存/初始化复用）
// ============================================================================

export const buildOplItemsFromPlain = (
  nodes: PlainNode[],
  edges: PlainEdge[]
): OplItem[] => {
  const aliveNodes = nodes.filter(node => !node.deleted);
  const nodeRecords: OplNodeRecord[] = aliveNodes.map(node => ({
    id: node.id,
    label: node.label,
    type: node.type,
    parentId: node.parentId || undefined,
    childGroupType: node.childGroupType,
    essence: node.essence,
    affiliation: node.affiliation,
    deleted: node.deleted,
  }));

  const ctx = buildOplContext(nodeRecords);

  const edgeRecords: OplEdgeRecord[] = edges
    .filter(edge => !edge.deleted)
    .map(edge => ({
      id: edge.id,
      sourceId: edge.sourceId,
      targetId: edge.targetId,
      type: edge.type,
      label: edge.label,
      linkQualifier: edge.linkQualifier,
      linkNegation: edge.linkNegation,
    }));

  return [
    ...buildNodeDefinitionItems(ctx),
    ...buildRelationItems(edgeRecords, ctx),
  ];
};
// 工具函数
// ============================================================================

/**
 * 规范化单行文本值（去除换行符）
 */
const normalizeSingleLineText = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.replace(/\r\n|\r|\n/g, '');
};

/**
 * 提取节点文本值
 */
const extractTextValue = (text: any): string => {
  if (!text) return '';
  if (typeof text === 'string') return normalizeSingleLineText(text);
  if (typeof text === 'object' && text.value !== undefined)
    return normalizeSingleLineText(text.value);
  return '';
};

/**
 * 提取有限数值，兼容 number / numeric string
 */
const getFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

/**
 * 生成唯一的图表ID
 */
let diagramCounter = 0;
const generateDiagramId = (): string => `diagram-${diagramCounter++}`;

/**
 * 兼容后端未回传 parentNodeId 的旧/中间态图层数据。
 * 规则：若当前图层节点里存在指向“本层之外”的唯一 parentGroupId，
 * 则可将其视为该子图对应的主节点 id。
 */
const inferLayerParentNodeId = (
  layer: OntologyGraphLayer,
  parentDiagramId: string | null
): string | null => {
  const explicitParentNodeId =
    typeof layer.parentNodeId === 'string' ? layer.parentNodeId.trim() : '';
  if (explicitParentNodeId) {
    return explicitParentNodeId;
  }

  // 根图层没有“由某个节点展开出来”的主节点关系，不做自动推断。
  if (parentDiagramId === null) {
    return null;
  }

  const layerNodeIds = new Set(
    (layer.graphData?.nodes || []).map(node => String(node.id))
  );
  const nodeParentMap = new Map<string, string>();
  (layer.graphData?.nodes || []).forEach(node => {
    const parentGroupId =
      typeof node.properties?.parentGroupId === 'string'
        ? node.properties.parentGroupId.trim()
        : '';
    nodeParentMap.set(String(node.id), parentGroupId);
  });

  // 优先识别“当前子图真正的主节点”：
  // 它应当被本层其他节点作为 parentGroupId 引用，
  // 且它自己不是本层另一个节点的普通子节点（允许其 parentGroupId 为空或指向层外）。
  const referencedInternalParentIds = Array.from(
    new Set(
      Array.from(nodeParentMap.values()).filter(
        parentGroupId => !!parentGroupId && layerNodeIds.has(parentGroupId)
      )
    )
  );
  const rootInternalParentIds = referencedInternalParentIds.filter(
    candidateId => {
      const candidateParentId = nodeParentMap.get(candidateId) || '';
      return !candidateParentId || !layerNodeIds.has(candidateParentId);
    }
  );
  if (rootInternalParentIds.length === 1) {
    return rootInternalParentIds[0];
  }

  const externalParentIds = Array.from(
    new Set(
      (layer.graphData?.nodes || [])
        .map(node => {
          const parentGroupId =
            typeof node.properties?.parentGroupId === 'string'
              ? node.properties.parentGroupId.trim()
              : '';
          if (!parentGroupId) return '';
          return layerNodeIds.has(parentGroupId) ? '' : parentGroupId;
        })
        .filter(Boolean)
    )
  );

  if (externalParentIds.length === 1) {
    return externalParentIds[0];
  }

  // 兼容某些子图把主节点本身也放进当前 layer 的场景：
  // 此时主节点通常是本层唯一一个没有 parentGroupId 的节点。
  const rootNodeIds = Array.from(nodeParentMap.entries())
    .map(node => {
      const [nodeId, parentGroupId] = node;
      return parentGroupId ? '' : nodeId;
    })
    .filter(Boolean);

  return rootNodeIds.length === 1 ? rootNodeIds[0] : null;
};

/**
 * 重置图表计数器（用于测试）
 */
export const resetDiagramCounter = () => {
  diagramCounter = 0;
};

// ============================================================================
// OPMEditor → OPMEditorCV 转换
// ============================================================================

/**
 * 转换单个节点：OPMNode → PlainNode
 */
const convertNodeToPlain = (
  node: OPMNode,
  parentId: string | null = null
): PlainNode => {
  const props = node.properties || {};

  // 确定节点类型
  let plainType: PlainNodeType = NODE_TYPE_TO_PLAIN[node.type] || 'object';
  if (props.isState) plainType = 'state';
  if (props.isValue) plainType = 'value';

  // 确定父节点ID
  let resolvedParentId = parentId;
  if (props.parentGroupId) {
    resolvedParentId = props.parentGroupId;
  }

  return {
    id: node.id,
    type: plainType,
    label:
      extractTextValue(node.text) || normalizeSingleLineText(props.name) || '',
    description:
      props.description !== undefined
        ? props.description
        : (props as any).label || '',
    namespace:
      typeof props.namespace === 'string' ? props.namespace.trim() : undefined,
    packageName:
      typeof props.packageName === 'string' ? props.packageName.trim() : undefined,
    codeName: props.codeName || '',
    tags: props.tags || [],
    objectType:
      props.objectType || (plainType === 'object' ? 'composite' : undefined),
    exampleValues: props.exampleValues || '',
    autonomy:
      plainType === 'process'
        ? {
            action: props.autonomy?.action || '',
            prompt: props.autonomy?.prompt || '',
            withTool: !!props.autonomy?.withTool,
          }
        : undefined,
    version:
      props.version ||
      (plainType === 'object' || plainType === 'process' ? '1.0' : undefined),
    updatedAt: props.updatedAt || '',
    updatedBy: props.updatedBy || [],
    relatedSystem: props.relatedSystem || '',
    parentId: resolvedParentId,
    essence: props.essence || Essence.Informatical,
    affiliation: props.affiliation || Affiliation.Systemic,
    isStructurePart:
      props.isStructurePart || props.isState || props.isValue || false,
    isSubelementOfMain: props.isSubelementOfMain || false,
    deleted: false,
    diagramId: null,
    width: node.width || props.width || null,
    height: node.height || props.height || null,
    // 保存原始信息以便回转
    _legacyType: node.type,
    _legacyProperties: { ...props },
    childGroupType: props.childGroupType || ChildGroupType.None,
  };
};

/**
 * 转换单条边：OPMEdge → PlainEdge
 */
const convertEdgeToPlain = (edge: OPMEdge): PlainEdge => {
  const props = edge.properties || {};

  // 从旧数据中解析“老编辑器边类型ID”
  // 注意：线上/历史数据存在 edge.type 与 properties.edgeType 不一致的情况（例如 edge.type=polyline，但 properties.edgeType=middle-arrow），
  // 旧组件以 edge.type 为准显示为“单向关联关系”，因此这里优先使用 edge.type 以保持行为一致。
  const resolveLegacyEdgeTypeKey = (): string => {
    const typeFromRoot = (edge as any).type || '';
    if (typeFromRoot && EDGE_TYPE_MAP[typeFromRoot]) return typeFromRoot;
    const typeFromProps = props.edgeType || '';
    if (typeFromProps && EDGE_TYPE_MAP[typeFromProps]) return typeFromProps;
    return '';
  };

  // 确定边类型
  let edgeType: EdgeType = EdgeType.Consumption;
  // 优先使用老编辑器的 edgeType（UI 类型 ID），避免 opmEdgeType（扩展字段）导致旧数据被错误映射
  const legacyEdgeTypeKey = resolveLegacyEdgeTypeKey();
  if (legacyEdgeTypeKey) {
    edgeType = EDGE_TYPE_MAP[legacyEdgeTypeKey];
  } else if (props.opmEdgeType) {
    if (Object.values(EdgeType).includes(props.opmEdgeType as EdgeType)) {
      edgeType = props.opmEdgeType as EdgeType;
    } else if (EDGE_TYPE_MAP[props.opmEdgeType]) {
      edgeType = EDGE_TYPE_MAP[props.opmEdgeType];
    }
  }

  // 兼容：若是从 OPMEditorCV 导出的旧格式数据，polyline 无法区分“消耗/工具/代理”等扩展类型，
  // 仅在这种情况下允许用 opmEdgeType 恢复扩展类型，避免影响“单向/对等关联”映射。
  if (
    edgeType === EdgeType.Tagged &&
    props.opmEdgeType &&
    (props.opmEdgeType === EdgeType.Consumption ||
      props.opmEdgeType === EdgeType.Instrument ||
      props.opmEdgeType === EdgeType.Agent)
  ) {
    edgeType = props.opmEdgeType;
  }

  return {
    id: edge.id,
    sourceId: edge.sourceNodeId,
    targetId: edge.targetNodeId,
    type: edgeType,
    label:
      normalizeSingleLineText(edge.text?.value) ||
      normalizeSingleLineText(props.label) ||
      '',
    tags: props.tags || [],
    description:
      props.description !== undefined ? props.description : props.label || '',
    version: props.version || '1.0',
    updatedAt: props.updatedAt || '',
    updatedBy: props.updatedBy || [],
    instanceFileName: props.instanceFileName || '',
    instanceFileUrl: props.instanceFileUrl || '',
    propagation: props.propagation ?? true,
    deleted: false,
    originalEdgeIds: [],
    derivedEdgeIds: [],
    preferedEdgeId: null,
    collection: 'original',
    linkQualifier: props.linkQualifier || LinkQualifier.None,
    linkNegation: props.linkNegation || LinkNegation.None,
    _legacyType: edge.type,
    _legacyProperties: { ...props },
  };
};

/**
 * 转换图层到 Cytoscape diagramJson 格式
 */
const convertGraphDataToDiagramJson = (
  graphData: OPMGraphData,
  nodeMap: Map<string, PlainNode>
): any => {
  const nodes = graphData.nodes.map(node => {
    const plainNode = nodeMap.get(node.id);
    const customWidth = getFiniteNumber(node.width ?? node.properties?.width);
    const customHeight = getFiniteNumber(
      node.height ?? node.properties?.height
    );
    return {
      data: {
        id: node.id,
        label:
          extractTextValue(node.text) ||
          normalizeSingleLineText(node.properties?.name) ||
          '',
        type: plainNode?.type || 'object',
        parent: node.properties?.parentGroupId || undefined,
        namespace: plainNode?.namespace || '',
        packageName: plainNode?.packageName || '',
        labelWidth: 80,
        MMRefId: node.id,
        ...(customWidth !== undefined ? { customWidth } : {}),
        ...(customHeight !== undefined ? { customHeight } : {}),
      },
      position: {
        x: node.x || 0,
        y: node.y || 0,
      },
      group: 'nodes',
      removed: false,
      selected: false,
      selectable: true,
      locked: false,
      grabbable: true,
      pannable: false,
      classes: '',
    };
  });

  const edges = graphData.edges.map(edge => {
    const props = edge.properties || {};
    // 同 convertEdgeToPlain：优先 edge.type，其次 properties.edgeType
    const typeKey =
      (edge as any).type && EDGE_TYPE_MAP[(edge as any).type]
        ? (edge as any).type
        : props.edgeType;
    const resolvedType =
      (typeKey && EDGE_TYPE_MAP[typeKey]) || EdgeType.Consumption;
    const linkQualifier = props.linkQualifier || LinkQualifier.None;
    const linkNegation = props.linkNegation || LinkNegation.None;

    return {
      data: {
        id: edge.id,
        label: edge.text?.value || props.label || '',
        type: resolvedType,
        source: edge.sourceNodeId,
        target: edge.targetNodeId,
        MMRefId: edge.id,
        linkQualifier,
        linkNegation,
        markerLabel: buildMarkerLabel(linkQualifier, linkNegation),
        markerColor: '#586D8C',
      },
      group: 'edges',
      removed: false,
      selected: false,
      selectable: true,
      locked: false,
      grabbable: true,
      pannable: true,
      classes: '',
    };
  });

  return {
    elements: { nodes, edges },
    zoom: 1,
    pan: { x: 0, y: 0 },
  };
};

/**
 * 递归转换图层树到 PlainDiagram 数组
 */
const convertLayersToDiagrams = (
  layers: OntologyGraphLayer[],
  parentDiagramId: string | null,
  nodeMap: Map<string, PlainNode>,
  edgeMap: Map<string, PlainEdge>,
  diagrams: PlainDiagram[],
  diagramIdMap: Map<string, string>
): void => {
  layers.forEach((layer, index) => {
    const diagramId = generateDiagramId();
    const resolvedParentNodeId = inferLayerParentNodeId(layer, parentDiagramId);
    diagramIdMap.set(layer.layerId, diagramId);

    // 确定标签
    let label: string | number = layer.layerName || 'SD';
    if (parentDiagramId === null && index === 0) {
      label = 'SD';
    }

    const plainNodesInLayer = layer.graphData.nodes
      .map(node => nodeMap.get(node.id))
      .filter(Boolean) as PlainNode[];
    const plainEdgesInLayer = layer.graphData.edges
      .map(edge => edgeMap.get(edge.id))
      .filter(Boolean) as PlainEdge[];

    const opl =
      layer.graphData.opl && layer.graphData.opl.length
        ? layer.graphData.opl
        : buildOplItemsFromPlain(plainNodesInLayer, plainEdgesInLayer);

    const diagram: PlainDiagram = {
      id: diagramId,
      label,
      // labelId 从 1 开始以兼容 DiagramTreeNode._findLowestAvailableId 的逻辑
      labelId: index + 1,
      parentId: parentDiagramId,
      childIds: [],
      // 兼容后端未回传 parentNodeId 的情况，避免重载后主节点与子图绑定关系丢失。
      mainNodeId: resolvedParentNodeId,
      diagramJson: convertGraphDataToDiagramJson(layer.graphData, nodeMap),
      opl,
      _layerId: layer.layerId,
      _layerName: layer.layerName,
      _nodeIdPrefix: layer.nodeIdPrefix,
    };

    diagrams.push(diagram);

    // 更新父图表的 childIds
    if (parentDiagramId) {
      const parentDiagram = diagrams.find(d => d.id === parentDiagramId);
      if (parentDiagram) {
        parentDiagram.childIds.push(diagramId);
      }
    }

    // 更新节点的 diagramId
    if (resolvedParentNodeId && nodeMap.has(resolvedParentNodeId)) {
      const node = nodeMap.get(resolvedParentNodeId)!;
      node.diagramId = diagramId;
    }

    // 递归处理子图层
    if (layer.children && layer.children.length > 0) {
      convertLayersToDiagrams(
        layer.children,
        diagramId,
        nodeMap,
        edgeMap,
        diagrams,
        diagramIdMap
      );
    }
  });
};

/**
 * 主转换函数：OPMEditor 格式 → OPMEditorCV Plain 格式
 */
export const convertOPMEditorToPlain = (
  data: OntologyGraphPayload | OntologyGraphTree | OPMGraphData
): PlainExportPayload => {
  resetDiagramCounter();

  // 规范化输入数据
  let layers: OntologyGraphTree;

  if ('version' in data && data.version === 'v2') {
    layers = data.data;
  } else if (Array.isArray(data) && data[0]?.graphData) {
    layers = data as OntologyGraphTree;
  } else {
    // 单层 OPMGraphData
    layers = [
      {
        layerId: '1',
        layerName: '本体图',
        graphData: data as OPMGraphData,
        children: [],
      },
    ];
  }

  // 收集所有节点
  const allNodes: PlainNode[] = [];
  const nodeMap = new Map<string, PlainNode>();

  const collectNodesFromLayers = (layerList: OntologyGraphLayer[]) => {
    layerList.forEach(layer => {
      layer.graphData.nodes.forEach(node => {
        if (!nodeMap.has(node.id)) {
          const plainNode = convertNodeToPlain(node);
          allNodes.push(plainNode);
          nodeMap.set(node.id, plainNode);
        }
      });
      if (layer.children) {
        collectNodesFromLayers(layer.children);
      }
    });
  };

  collectNodesFromLayers(layers);

  // 兼容后端精简返回：若未显式返回 childGroupType，则根据已恢复的父子关系补推断。
  allNodes.forEach(node => {
    if (node.childGroupType && node.childGroupType !== ChildGroupType.None) {
      return;
    }

    const children = allNodes.filter(child => child.parentId === node.id);
    const hasValue = children.some(child => child.type === 'value');
    const hasState = children.some(child => child.type === 'state');
    const hasInZoomChild = children.some(
      child =>
        (child.type === 'object' || child.type === 'process') &&
        child.isSubelementOfMain
    );

    if (hasValue) node.childGroupType = ChildGroupType.Value;
    else if (hasState) node.childGroupType = ChildGroupType.State;
    else if (hasInZoomChild) node.childGroupType = ChildGroupType.InZoom;
  });

  recalculateNamespacesForPlainNodes(allNodes);

  // 收集所有边
  const allEdges: PlainEdge[] = [];
  const edgeMap = new Map<string, PlainEdge>();

  const collectEdgesFromLayers = (layerList: OntologyGraphLayer[]) => {
    layerList.forEach(layer => {
      layer.graphData.edges.forEach(edge => {
        if (!edgeMap.has(edge.id)) {
          const plainEdge = convertEdgeToPlain(edge);
          allEdges.push(plainEdge);
          edgeMap.set(edge.id, plainEdge);
        }
      });
      if (layer.children) {
        collectEdgesFromLayers(layer.children);
      }
    });
  };

  collectEdgesFromLayers(layers);

  // 转换图层到图表
  const diagrams: PlainDiagram[] = [];
  const diagramIdMap = new Map<string, string>();
  convertLayersToDiagrams(
    layers,
    null,
    nodeMap,
    edgeMap,
    diagrams,
    diagramIdMap
  );

  // 计算 eleCounter
  const maxNodeNum = allNodes.reduce((max, node) => {
    const match = node.id.match(/(\d+)$/);
    return match ? Math.max(max, parseInt(match[1], 10)) : max;
  }, 0);
  const maxEdgeNum = allEdges.reduce((max, edge) => {
    const match = edge.id.match(/(\d+)$/);
    return match ? Math.max(max, parseInt(match[1], 10)) : max;
  }, 0);

  return {
    version: 1,
    nodes: allNodes,
    originalEdges: allEdges,
    derivedEdges: [],
    diagrams,
    rootDiagramId: diagrams[0]?.id || 'diagram-0',
    eleCounter: String(Math.max(maxNodeNum, maxEdgeNum) + 1),
    _convertedFrom: 'OPMEditor',
  };
};

// ============================================================================
// OPMEditorCV → OPMEditor 转换
// ============================================================================

/**
 * 转换 PlainNode → OPMNode
 */
const convertPlainToNode = (
  plainNode: PlainNode,
  diagramJson: any,
  childrenMap: Map<string, string[]>
): OPMNode => {
  // 从 diagramJson 获取位置信息
  let x = 0,
    y = 0;
  const cyNode = diagramJson?.elements?.nodes?.find(
    (n: any) => n.data?.id === plainNode.id || n.data?.MMRefId === plainNode.id
  );
  if (cyNode?.position) {
    x = cyNode.position.x;
    y = cyNode.position.y;
  }
  const diagramWidth =
    getFiniteNumber(cyNode?.data?.customWidth) ??
    getFiniteNumber(plainNode.width);
  const diagramHeight =
    getFiniteNumber(cyNode?.data?.customHeight) ??
    getFiniteNumber(plainNode.height);

  // 确定节点类型
  let nodeType = plainNode._legacyType || PLAIN_TYPE_TO_NODE[plainNode.type];

  // 如果是分组节点（有子节点），使用 dynamic-group
  const children = childrenMap.get(plainNode.id) || [];
  if (children.length > 0 && plainNode.type === 'object') {
    nodeType = 'dynamic-group';
  }

  // 构建 properties
  const legacyProperties = { ...(plainNode._legacyProperties || {}) };
  // Plain 导出的 description 要覆盖/补齐到 legacyProperties，确保往返转换可持久化
  if (plainNode.description !== undefined) {
    legacyProperties.description = plainNode.description;
  }
  // Plain 导出的扩展字段要同步到 legacyProperties
  if (plainNode.codeName !== undefined)
    legacyProperties.codeName = plainNode.codeName;
  if (plainNode.tags !== undefined) legacyProperties.tags = plainNode.tags;
  if (plainNode.objectType !== undefined)
    legacyProperties.objectType = plainNode.objectType;
  if (plainNode.exampleValues !== undefined)
    legacyProperties.exampleValues = plainNode.exampleValues;
  if (plainNode.autonomy !== undefined)
    legacyProperties.autonomy = plainNode.autonomy;
  if (plainNode.version !== undefined)
    legacyProperties.version = plainNode.version;
  if (plainNode.updatedAt !== undefined)
    legacyProperties.updatedAt = plainNode.updatedAt;
  if (plainNode.updatedBy !== undefined)
    legacyProperties.updatedBy = plainNode.updatedBy;
  if (plainNode.relatedSystem !== undefined)
    legacyProperties.relatedSystem = plainNode.relatedSystem;
  if (plainNode.namespace !== undefined)
    legacyProperties.namespace = plainNode.namespace;
  if (plainNode.packageName !== undefined)
    legacyProperties.packageName = plainNode.packageName;
  const properties: OPMNode['properties'] = {
    name: plainNode.label,
    description: plainNode.description ?? legacyProperties.description ?? '',
    namespace: plainNode.namespace ?? legacyProperties.namespace,
    packageName: plainNode.packageName ?? legacyProperties.packageName,
    codeName: plainNode.codeName ?? legacyProperties.codeName,
    tags: plainNode.tags ?? legacyProperties.tags ?? [],
    objectType: plainNode.objectType ?? legacyProperties.objectType,
    exampleValues: plainNode.exampleValues ?? legacyProperties.exampleValues,
    autonomy:
      plainNode.type === 'process'
        ? plainNode.autonomy ??
          legacyProperties.autonomy ?? {
            action: '',
            prompt: '',
            withTool: false,
          }
        : undefined,
    version:
      plainNode.version ??
      legacyProperties.version ??
      (plainNode.type === 'object' || plainNode.type === 'process'
        ? '1.0'
        : undefined),
    updatedAt: plainNode.updatedAt ?? legacyProperties.updatedAt,
    updatedBy: plainNode.updatedBy ?? legacyProperties.updatedBy,
    relatedSystem: plainNode.relatedSystem ?? legacyProperties.relatedSystem,
    nodeType: nodeType,
    // 保存 OPMEditorCV 扩展属性
    essence: plainNode.essence,
    affiliation: plainNode.affiliation,
    isStructurePart: plainNode.isStructurePart,
    isSubelementOfMain: plainNode.isSubelementOfMain,
    childGroupType: plainNode.childGroupType,
    // 合并原始属性
    ...legacyProperties,
    width: diagramWidth,
    height: diagramHeight,
  };

  // 状态节点特有属性
  if (plainNode.type === 'state') {
    properties.isState = true;
    properties.nodeType = 'state';
    if (plainNode.parentId) {
      properties.parentGroupId = plainNode.parentId;
    }
  }

  // 值节点特有属性
  if (plainNode.type === 'value') {
    properties.isValue = true;
    properties.nodeType = 'value';
    if (plainNode.parentId) {
      properties.parentGroupId = plainNode.parentId;
    }
  }

  // 分组节点特有属性
  if (nodeType === 'dynamic-group') {
    properties.isGroup = true;
    properties.isEntityGroup = true;
    properties.originalType = 'dynamic-group';
    properties.children = children;
    properties.isRestrict = true;
    properties.autoResize = false;
    properties.collapsible = false;
    properties.isCollapsed = false;
  }

  // 无论节点类型，只要有父节点都写入 parentGroupId，确保往返转换时父子关系不丢失
  if (plainNode.parentId) {
    properties.parentGroupId = plainNode.parentId;
  }

  return {
    id: plainNode.id,
    type: nodeType,
    x,
    y,
    width: diagramWidth,
    height: diagramHeight,
    text: {
      x,
      y,
      value: plainNode.label,
    },
    properties,
    children: children.length > 0 ? children : undefined,
  };
};

/**
 * 转换 PlainEdge → OPMEdge
 */
const convertPlainToEdge = (plainEdge: PlainEdge): OPMEdge => {
  const normalizedType =
    (plainEdge.type as EdgeType | 'Influence') === 'Influence'
      ? EdgeType.Effect
      : plainEdge.type === EdgeType.Effect &&
          (plainEdge._legacyType === 'bidirectional-arrow' ||
            plainEdge._legacyType === 'Bidirectional-arrow')
        ? EdgeType.Bidirectional
        : plainEdge.type;

  const legacyEdgeType = EDGE_TYPE_REVERSE_MAP[normalizedType] || 'polyline';
  const opmEdgeTypeValue =
    normalizedType === EdgeType.Bidirectional
      ? 'Bidirectional-arrow'
      : normalizedType;
  // 避免出现 edge.type 与 properties.edgeType 不一致（会导致新旧编辑器显示不一致）
  // 仅当 _legacyType 能映射回当前 plainEdge.type 时才沿用，否则使用 reverseMap 的结果
  const resolvedRootType =
    plainEdge._legacyType &&
    EDGE_TYPE_MAP[plainEdge._legacyType] === normalizedType
      ? plainEdge._legacyType
      : legacyEdgeType;

  const legacyProperties = { ...(plainEdge._legacyProperties || {}) };
  if (plainEdge.tags !== undefined) legacyProperties.tags = plainEdge.tags;
  if (plainEdge.version !== undefined)
    legacyProperties.version = plainEdge.version;
  if (plainEdge.updatedAt !== undefined)
    legacyProperties.updatedAt = plainEdge.updatedAt;
  if (plainEdge.updatedBy !== undefined)
    legacyProperties.updatedBy = plainEdge.updatedBy;
  if (plainEdge.description !== undefined)
    legacyProperties.description = plainEdge.description;
  if (plainEdge.instanceFileName !== undefined)
    legacyProperties.instanceFileName = plainEdge.instanceFileName;
  if (plainEdge.instanceFileUrl !== undefined)
    legacyProperties.instanceFileUrl = plainEdge.instanceFileUrl;

  return {
    id: plainEdge.id,
    type: resolvedRootType,
    sourceNodeId: plainEdge.sourceId,
    targetNodeId: plainEdge.targetId,
    properties: {
      edgeType: legacyEdgeType,
      opmEdgeType: opmEdgeTypeValue,
      propagation: plainEdge.propagation,
      label: plainEdge.label,
      description: plainEdge.description ?? legacyProperties.description,
      tags: plainEdge.tags ?? legacyProperties.tags,
      version: plainEdge.version ?? legacyProperties.version ?? '1.0',
      updatedAt: plainEdge.updatedAt ?? legacyProperties.updatedAt,
      updatedBy: plainEdge.updatedBy ?? legacyProperties.updatedBy,
      instanceFileName:
        plainEdge.instanceFileName ?? legacyProperties.instanceFileName,
      instanceFileUrl:
        plainEdge.instanceFileUrl ?? legacyProperties.instanceFileUrl,
      linkQualifier: plainEdge.linkQualifier ?? LinkQualifier.None,
      linkNegation: plainEdge.linkNegation ?? LinkNegation.None,
      ...legacyProperties,
    },
    text: plainEdge.label
      ? {
          x: 0,
          y: 0,
          value: plainEdge.label,
        }
      : undefined,
  };
};

/**
 * 递归转换图表到图层
 */
const convertDiagramsToLayers = (
  diagrams: PlainDiagram[],
  parentDiagramId: string | null,
  nodeMap: Map<string, PlainNode>,
  childrenMap: Map<string, string[]>,
  edgeMap: Map<string, PlainEdge[]>,
  parentLayerId?: string
): OntologyGraphLayer[] => {
  // 找到当前层级的图表
  const currentLevelDiagrams = diagrams.filter(
    d => d.parentId === parentDiagramId
  );

  return currentLevelDiagrams.map((diagram, index) => {
    const layerId =
      diagram._layerId ||
      (parentLayerId ? `${parentLayerId}-${index + 1}` : '1');
    const layerName =
      diagram._layerName ||
      (typeof diagram.label === 'string'
        ? diagram.label
        : `图层 ${diagram.label}`);

    // 收集该图表中的节点ID
    const diagramNodeIds = new Set<string>();
    if (diagram.diagramJson?.elements?.nodes) {
      diagram.diagramJson.elements.nodes.forEach((n: any) => {
        const nodeId = n.data?.MMRefId || n.data?.id;
        if (nodeId) diagramNodeIds.add(nodeId);
      });
    }

    // 转换节点
    const nodes: OPMNode[] = [];
    diagramNodeIds.forEach(nodeId => {
      const plainNode = nodeMap.get(nodeId);
      if (plainNode && !plainNode.deleted) {
        nodes.push(
          convertPlainToNode(plainNode, diagram.diagramJson, childrenMap)
        );
      }
    });

    // 收集该图表中的边
    const edgeElements = diagram.diagramJson?.elements?.edges || [];
    const edges: OPMEdge[] = [];
    const plainEdgesInDiagram: PlainEdge[] = [];
    edgeElements.forEach((edgeEl: any) => {
      const edgeId = edgeEl?.data?.MMRefId || edgeEl?.data?.id;
      if (!edgeId) return;
      const candidates = edgeMap.get(edgeId) || [];
      if (!candidates.length) return;
      const sourceId = edgeEl?.data?.source;
      const targetId = edgeEl?.data?.target;
      const matched =
        sourceId && targetId
          ? candidates.find(
              candidate =>
                candidate.sourceId === sourceId &&
                candidate.targetId === targetId
            )
          : undefined;
      const picked = matched || candidates[0];
      if (!picked || picked.deleted) return;
      edges.push(convertPlainToEdge(picked));
      plainEdgesInDiagram.push(picked);
    });

    const plainNodesInDiagram = Array.from(diagramNodeIds)
      .map(id => nodeMap.get(id))
      .filter(Boolean) as PlainNode[];

    const opl =
      (diagram as PlainDiagram).opl && (diagram as PlainDiagram).opl!.length
        ? (diagram as PlainDiagram).opl
        : buildOplItemsFromPlain(plainNodesInDiagram, plainEdgesInDiagram);

    // 递归处理子图层
    const children = convertDiagramsToLayers(
      diagrams,
      diagram.id,
      nodeMap,
      childrenMap,
      edgeMap,
      layerId
    );

    return {
      layerId,
      layerName,
      graphData: {
        nodes,
        edges,
        opl,
      },
      children,
      parentLayerId,
      parentNodeId: diagram.mainNodeId || undefined,
      nodeIdPrefix: diagram._nodeIdPrefix,
    };
  });
};

/**
 * 主转换函数：OPMEditorCV Plain 格式 → OPMEditor 格式
 */
export const convertPlainToOPMEditor = (
  data: PlainExportPayload
): OntologyGraphPayload => {
  // 构建节点映射
  const nodeMap = new Map<string, PlainNode>();
  data.nodes.forEach(node => nodeMap.set(node.id, node));

  // 构建子节点映射
  const childrenMap = new Map<string, string[]>();
  data.nodes.forEach(node => {
    if (node.parentId) {
      const children = childrenMap.get(node.parentId) || [];
      children.push(node.id);
      childrenMap.set(node.parentId, children);
    }
  });

  // 构建边映射
  const edgeMap = new Map<string, PlainEdge[]>();
  [...data.originalEdges, ...data.derivedEdges].forEach(edge => {
    const bucket = edgeMap.get(edge.id) || [];
    bucket.push(edge);
    edgeMap.set(edge.id, bucket);
  });

  // 转换图表到图层
  const layers = convertDiagramsToLayers(
    data.diagrams,
    null,
    nodeMap,
    childrenMap,
    edgeMap
  );

  return {
    version: 'v2',
    data:
      layers.length > 0
        ? layers
        : [
            {
              layerId: '1',
              layerName: '本体图',
              graphData: { nodes: [], edges: [], opl: [] },
              children: [],
            },
          ],
  };
};

// ============================================================================
// 统一适配器接口
// ============================================================================

export const isPlainFormat = (data: any): data is PlainExportPayload => {
  return !!(
    data &&
    typeof data === 'object' &&
    data.version === 1 &&
    Array.isArray(data.nodes) &&
    Array.isArray(data.originalEdges) &&
    Array.isArray(data.derivedEdges) &&
    Array.isArray(data.diagrams) &&
    typeof data.rootDiagramId === 'string'
  );
};

export const isOPMEditorFormat = (
  data: any
): data is OntologyGraphPayload | OntologyGraphTree | OPMGraphData => {
  if (!data) return false;

  if (
    typeof data === 'object' &&
    'version' in data &&
    data.version === 'v2' &&
    Array.isArray(data.data)
  ) {
    return true;
  }

  if (Array.isArray(data)) {
    return data.every(
      layer =>
        layer &&
        typeof layer === 'object' &&
        Array.isArray(layer.children) &&
        layer.graphData &&
        Array.isArray(layer.graphData.nodes) &&
        Array.isArray(layer.graphData.edges)
    );
  }

  return (
    typeof data === 'object' &&
    Array.isArray(data.nodes) &&
    Array.isArray(data.edges)
  );
};

/**
 * 自动检测格式并转换为 OPMEditorCV Plain 格式
 */
export const toPlainFormat = (data: any): PlainExportPayload | null => {
  if (isPlainFormat(data)) {
    return data;
  }
  if (isOPMEditorFormat(data)) {
    return convertOPMEditorToPlain(data);
  }
  return null;
};

/**
 * 自动检测格式并转换为 OPMEditor 格式
 */
export const toOPMEditorFormat = (data: any): OntologyGraphPayload | null => {
  if (isOPMEditorFormat(data)) {
    if ('version' in data && data.version === 'v2') {
      return data;
    }
    // 将其他 OPMEditor 格式规范化为 OntologyGraphPayload
    return convertPlainToOPMEditor(convertOPMEditorToPlain(data));
  }
  if (isPlainFormat(data)) {
    return convertPlainToOPMEditor(data);
  }
  return null;
};

/**
 * 检测数据格式类型
 */
export const detectDataFormat = (
  data: any
): 'OPMEditor' | 'OPMEditorCV' | 'unknown' => {
  if (isPlainFormat(data)) return 'OPMEditorCV';
  if (isOPMEditorFormat(data)) return 'OPMEditor';
  return 'unknown';
};
